import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Send, Loader2, User, Shield } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CommentThread = ({ orderId, currentUser = 'client', currentName = 'Client' }) => {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const fetchMessages = async () => {
    try {
      const resp = await axios.get(`${API}/orders/${orderId}/messages`);
      setMessages(resp.data.messages || []);
    } catch (e) { /* message fetch non-critical: thread shows empty state */ }
  };

  useEffect(() => {
    if (orderId) fetchMessages();
    const interval = setInterval(fetchMessages, 15000); // poll every 15s
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const resp = await axios.post(`${API}/orders/${orderId}/messages`, { text: text.trim(), sender: currentUser, sender_name: currentName });
      setMessages(prev => [...prev, resp.data]);
      setText('');
    } catch (err) { setText(prev => prev); /* send failed: keep text in input so user can retry */ }
    finally { setSending(false); }
  };

  const formatTime = (ts) => {
    try { return new Date(ts).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
    catch (e) { return ts; /* invalid date string — show raw timestamp */ }
  };

  return (
    <div data-testid="comment-thread">
      {/* Messages */}
      <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1 mb-3" data-testid="messages-list">
        {messages.length === 0 && (
          <p className="text-xs text-slate-400 text-center py-4">No messages yet. Start the conversation.</p>
        )}
        {messages.map((msg) => {
          const isAdmin = msg.sender === 'admin';
          return (
            <motion.div key={msg.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} className={`flex gap-2.5 ${isAdmin ? '' : 'flex-row-reverse'}`}>
              <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${isAdmin ? 'bg-brand-blue/10' : 'bg-brand-orange/10'}`}>
                {isAdmin ? <Shield className="w-3.5 h-3.5 text-brand-blue" /> : <User className="w-3.5 h-3.5 text-brand-orange" />}
              </div>
              <div className={`max-w-[75%] ${isAdmin ? '' : 'text-right'}`}>
                <div className={`inline-block px-3.5 py-2.5 rounded-xl text-sm ${isAdmin ? 'bg-slate-100 text-slate-800 rounded-tl-sm' : 'bg-brand-blue/10 text-slate-800 rounded-tr-sm'}`}>
                  {msg.text}
                </div>
                <p className="text-[10px] text-slate-400 mt-1">
                  {msg.sender_name} &middot; {formatTime(msg.timestamp)}
                </p>
              </div>
            </motion.div>
          );
        })}
        <div ref={endRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSend} className="flex gap-2">
        <input
          value={text} onChange={e => setText(e.target.value)}
          placeholder="Type a message..."
          className="flex-1 px-3.5 py-2.5 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue"
          data-testid="message-input"
        />
        <button type="submit" disabled={!text.trim() || sending}
          className="px-4 py-2.5 rounded-xl bg-brand-blue text-white text-sm font-semibold hover:bg-brand-blue-dark transition-colors disabled:opacity-40 inline-flex items-center gap-1.5"
          data-testid="send-message-btn">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </form>
    </div>
  );
};
